export const CLOSED_STATUSES = new Set(["closed"]);
export const LOST_STATUSES = new Set(["cancelled", "canceled", "terminated", "expired", "lost"]);

export function normalize(value) {
  return String(value || "").trim().toLowerCase();
}

export function normalizeRole(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

export function categoryFor(transaction) {
  const side = normalize(transaction?.transactionSide);
  const type = normalize(transaction?.transactionType);
  if (side.includes("referral") || type.includes("referral")) return "referrals";
  if (side === "seller" || side.includes("listing") || type.includes("listing")) return "sellers";
  return "buyers";
}

export function transactionTypeLabel(transaction) {
  const category = categoryFor(transaction);
  if (category === "sellers") return "Seller";
  if (category === "referrals") return "Referral";
  return "Buyer";
}

export function transactionTypeLetter(transaction) {
  return transactionTypeLabel(transaction).slice(0, 1);
}

export function transactionCoordinatorStatus(transaction) {
  return transaction?.tcAssignmentStatus || (transactionCoordinatorEmail(transaction) ? "Assigned" : "Not assigned");
}

export function transactionCoordinatorName(transaction) {
  if (normalize(transaction?.tcAssignmentStatus) === "ambiguous") return "Assignment unavailable";
  return transaction?.tcName
    || transaction?.assignedTcName
    || transaction?.transactionCoordinatorName
    || "Not assigned";
}

export function transactionCoordinatorEmail(transaction) {
  if (normalize(transaction?.tcAssignmentStatus) === "ambiguous") return "";
  return transaction?.tcEmail
    || transaction?.assignedTcEmail
    || transaction?.transactionCoordinatorEmail
    || "";
}

export function transactionDate(transaction) {
  return transaction?.closingDate || "";
}

export function parseDate(value) {
  if (!value) return null;
  const source = String(value);
  const date = new Date(source.length === 10 ? `${source}T12:00:00` : source);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function matchesClosingDateRange(transaction, fromValue = "", toValue = "") {
  const hasFrom = Boolean(fromValue);
  const hasTo = Boolean(toValue);
  if (!hasFrom && !hasTo) return true;

  const closingDate = parseDate(transaction?.closingDate);
  if (!closingDate) return false;

  if (hasFrom) {
    const fromDate = parseDate(fromValue);
    if (!fromDate || closingDate < fromDate) return false;
  }

  if (hasTo) {
    const toDate = parseDate(toValue);
    if (!toDate) return false;
    toDate.setHours(23, 59, 59, 999);
    if (closingDate > toDate) return false;
  }

  return true;
}

export function formatDate(value) {
  const date = parseDate(value);
  if (!date) return "Not set";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(date);
}

export function formatDateTime(value) {
  const date = parseDate(value);
  if (!date) return "Date unavailable";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

export function money(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(Number(value || 0));
}

export function unique(values) {
  return [...new Set(values.filter(Boolean))].sort((left, right) => left.localeCompare(right));
}

export function statusTone(status) {
  const value = normalize(status);
  if (CLOSED_STATUSES.has(value) || value.includes("complete") || value.includes("verified")) return "complete";
  if (LOST_STATUSES.has(value) || value.includes("locked") || value.includes("unavailable")) return "muted";
  if (value.includes("overdue") || value.includes("incomplete") || value.includes("unverified")) return "danger";
  if (value.includes("review") || value.includes("pending") || value.includes("due soon")) return "attention";
  return "active";
}

export function isPortfolioRole(user) {
  return new Set(["OWNER", "SYSTEM_OWNER", "SYSTEMOWNER", "PARTNER", "DIRECTOR_OF_OPERATIONS", "LEADERSHIP"])
    .has(normalizeRole(user?.role));
}

export function isTransactionCoordinator(user) {
  return normalizeRole(user?.role) === "TRANSACTION_COORDINATOR";
}

export function canViewTransaction(user, transaction) {
  if (!transaction) return false;
  if (isPortfolioRole(user)) return true;

  const userEmail = normalize(user?.email);
  if (!userEmail) return false;

  if (isTransactionCoordinator(user)) {
    const tcEmail = normalize(transactionCoordinatorEmail(transaction));
    return Boolean(tcEmail && tcEmail === userEmail);
  }

  return normalize(transaction?.agentEmail) === userEmail;
}

export function isApproved(transaction) {
  return normalize(transaction?.intakeStatus) === "approved";
}

function relationshipId(record) {
  return record?.transactionId
    ?? record?.dealId
    ?? record?.linkedTransactionId
    ?? "";
}

export function relatedByTransactionId(record, transaction) {
  const recordId = relationshipId(record);
  if (!recordId || !transaction?.id) return false;
  return String(recordId) === String(transaction.id);
}

export function relatedToTransaction(record, transaction) {
  if (!record || !transaction) return false;

  const recordId = relationshipId(record);
  if (recordId) return String(recordId) === String(transaction.id);

  const address = normalize(record.propertyAddress);
  return Boolean(address && address === normalize(transaction.propertyAddress));
}

function numericOrder(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : Number.MAX_SAFE_INTEGER;
}

export function checklistPhaseCompletion(phase) {
  const tasks = Array.isArray(phase?.tasks) ? phase.tasks : [];
  return {
    completed: tasks.reduce((count, task) => count + (task?.completed === true ? 1 : 0), 0),
    total: tasks.length
  };
}

export function structuredChecklistPhases(tasks, phaseDataAvailable) {
  if (!phaseDataAvailable) return null;
  if (!tasks.length) return [];

  const phases = new Map();
  for (const task of tasks) {
    const phaseId = String(task.phaseId || "").trim();
    const phaseName = String(task.phaseName || "").trim();
    if (!phaseId || !phaseName) return null;

    const existing = phases.get(phaseId);
    if (existing && existing.name !== phaseName) return null;

    const phase = existing || {
      id: phaseId,
      name: phaseName,
      order: numericOrder(task.phaseOrder),
      tasks: []
    };
    phase.order = Math.min(phase.order, numericOrder(task.phaseOrder));
    phase.tasks.push(task);
    phases.set(phaseId, phase);
  }

  return [...phases.values()]
    .map(phase => ({
      ...phase,
      tasks: [...phase.tasks].sort((left, right) => {
        const orderDifference = numericOrder(left.taskOrder) - numericOrder(right.taskOrder);
        if (orderDifference) return orderDifference;
        const leftDue = parseDate(left.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const rightDue = parseDate(right.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        if (leftDue !== rightDue) return leftDue - rightDue;
        return String(left.title || "").localeCompare(String(right.title || ""));
      })
    }))
    .sort((left, right) => {
      if (left.order !== right.order) return left.order - right.order;
      return left.name.localeCompare(right.name);
    });
}

export function dateVerification(transaction) {
  const raw = normalize(
    transaction?.closingDateVerificationStatus
      || transaction?.closingDateStatus
      || transaction?.dateVerificationStatus
  );

  if (raw.includes("verified") && !raw.includes("unverified")) {
    return { label: "Verified", tone: "complete" };
  }

  return { label: "Unverified", tone: "danger" };
}
